import { Box, DialogActions, Grid } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import update from 'immutability-helper';
import find from 'lodash/find';
import range from 'lodash/range';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, FormWithRedirect, SaveButton, useNotify } from 'react-admin';
import { apiGet, apiPost } from '../../brainApi';
import { getZ3PConfigValue } from '../../helper';
import useStyles from '../../QuickActions/styles';
import { nonautomobileTabs } from '../../VehicleConstants';
import { getInitialVehicleTab, getTabNames } from '../../vehicleHelper';
import HeavyDutyForm from './HeavyDutyForm';
import LicenseForm from './LicenseForm';
import NonAutomobileForm from './NonAutomobileForm';
import VehicleInputMethodTabs from './VehicleInputMethodTabs';
import VehicleListSelection from './VehicleListSelection';
import VINForm from './VINForm';
import YMMTForm from './YMMTForm';

// To select an existing vehicle or create a new one
const AddVehicleForm = ({
  vehicleData,
  customerId,
  customerType,
  onCreating,
  onSuccess,
  onError,
  onSelectVehicle,
  onSaveSelectedVehicle,
  onSkip,
  vehicleList = [],
}) => {
  const notify = useNotify();
  const classes = useStyles();

  const {
    available_vehicle_entry_tabs: availableVehicleEntryTabs,
    default_vehicle_entry_tab: defaultVehicleEntryTab,
    is_using_heavy_duty: isUsingHeavyDuty,
  } = getZ3PConfigValue();

  const initialTab = getInitialVehicleTab(
    availableVehicleEntryTabs,
    defaultVehicleEntryTab,
  );

  const [tabToLabelMap, setTabToLabelMap] = useState({});
  const [labelActive, setLabelActive] = useState(initialTab);
  const [isCreating, setIsCreating] = useState(false);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [nonautomobileData, setNonautomobileData] = useState({});
  // If coming from ConvertToAppointmentButton, set initial vehicle data
  const [vehicle, setVehicle] = useState(
    vehicleData
      ? {
          ...vehicleData,
          custom: {
            make: '',
            model: '',
            trim: '',
            heavyMake: '',
            heavyEngineMake: '',
            fuelType: '',
          },
        }
      : null,
  );

  // Always show the future year as the top end of the range (ex: in 2021, show up to 2022)
  const currentYear = Number(moment().format('YYYY'));
  const startYear = [...nonautomobileTabs, 'heavy'].includes(labelActive)
    ? currentYear - 100
    : 1990;
  const years = range(currentYear + 1, startYear).map((year) => ({
    id: `${year}`,
    name: `${year}`,
  }));
  const [makeList, setMakeList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [trimList, setTrimList] = useState([]);

  const nonautomobileTouchedFields = nonautomobileTabs.reduce((obj, tab) => {
    const map = obj;
    map[tab] = {
      make: false,
    };
    return map;
  }, {});
  // Touched state for inputs in tabs with same make/model input fields
  const [touched, setTouched] = useState({
    year: {
      make: false,
      model: false,
      trim: false,
    },
    ...nonautomobileTouchedFields,
  });

  const existingVehicleIds = new Set(vehicleList.map((v) => v.id));
  // If there are no customer vehicles OR there is existing vehicle data not in the customer vehicles list show new vehicle creation form
  const [isAddingVehicle, setIsAddingVehicle] = useState(
    !vehicleList.length
      ? true
      : !!vehicle && !existingVehicleIds.has(vehicle.vehicle_id),
  );

  useEffect(() => {
    setTabToLabelMap(getTabNames(availableVehicleEntryTabs, isUsingHeavyDuty));
  }, []);

  useEffect(() => {
    // When nonautomobile tab is changed, clear make/model lists
    setMakeList([{ id: 'other', name: 'Other' }]);
    setModelList([{ id: 'other', name: 'Other' }]);
    // Then fetch the appropriate make/model lists
    if (nonautomobileTabs.includes(labelActive)) {
      apiGet(`/other-vehicle/${labelActive}`)
        .then((res) => {
          if (res.Status === 'Success') {
            setNonautomobileData(res.Data);
            setMakeList([
              ...Object.keys(res.Data).map((make) => ({
                id: make,
                name: make,
              })),
              { id: 'other', name: 'Other' },
            ]);
          } else {
            notify(`Error: ${res.Message}`);
          }
        })
        .catch((error) => console.log(error));
    }
  }, [labelActive, notify]);

  const handleChange = (field, value) => {
    const newVehicle = {
      ...vehicle,
      [labelActive]: {
        ...vehicle?.[labelActive],
        [field]: value,
      },
    };

    if (field === 'year' && labelActive === 'year') {
      // If year is changed in year tab, clear make, model, and trim unless 'other' is selected
      newVehicle[labelActive].make = '';
      newVehicle[labelActive].model = '';
      newVehicle[labelActive].trim = '';

      const updatedTouched = update(touched, {
        [labelActive]: {
          $set: {
            make: false,
            model: false,
            trim: false,
          },
        },
      });
      setTouched(updatedTouched);
    }

    if (field === 'make') {
      // If make is changed, clear model and trim unless 'other' is selected
      newVehicle[labelActive].model = value === 'other' ? 'other' : '';
      if (labelActive === 'year') {
        newVehicle[labelActive].trim = value === 'other' ? 'other' : '';
      }

      const updatedTouched = update(touched, {
        [labelActive]: {
          $set: {
            model: false,
            trim: false,
          },
        },
      });
      setTouched(updatedTouched);
    }

    if (field === 'model') {
      // If model is changed, clear trim unless 'other' is selected
      newVehicle[labelActive].trim = value === 'other' ? 'other' : '';

      const updatedTouched = update(touched, {
        [labelActive]: {
          $set: {
            trim: false,
          },
        },
      });
      setTouched(updatedTouched);
    }

    if (field === 'trim' && value !== 'other') {
      const selectedTrim = trimList.find((t) => t.id === value);
      if (selectedTrim) {
        newVehicle[labelActive] = {
          ...newVehicle?.[labelActive],
          motor_base_vehicle_id: selectedTrim.motorBaseVehicleId,
          motor_vehicle_id: selectedTrim.motorVehicleId,
        };
      }
    }

    setVehicle(newVehicle);
  };

  const loadMakes = (year) => {
    apiGet(`/vehicles/makes/${year}`).then((makes) => {
      setMakeList([
        ...makes.map((make) => ({ id: make.makeId, name: make.makeName })),
        { id: 'other', name: 'Other' },
      ]);
      setModelList([]);
      setTrimList([]);
    });
  };

  const loadModels = (makeId) => {
    apiGet(`/vehicles/models/${vehicle.year.year}/${makeId}`).then((models) => {
      setModelList([
        ...models.map((model) => ({
          id: model.modelId,
          name: model.modelName,
        })),
        { id: 'other', name: 'Other' },
      ]);
      setTrimList([]);
    });
  };

  const loadTrims = (modelId) => {
    apiGet(
      `/vehicles/trims/${vehicle.year.year}/${vehicle.year.make}/${modelId}`,
    ).then((trims) => {
      setTrimList([
        ...trims.map((trim) => ({
          id: trim.submodelId,
          name: trim.submodelName,
          motorBaseVehicleId: trim.baseVehicleId,
          motorVehicleId: trim.vehicleId,
        })),
        { id: 'other', name: 'Other' },
      ]);
    });
  };

  const handleHeavyChange = (field) => (value) => {
    setVehicle((prevVehicle) => ({
      ...prevVehicle,
      heavy: {
        ...prevVehicle?.heavy,
        [field]: value,
      },
    }));
  };

  const handleTouched = (key) => () => {
    const updatedTouched = update(touched, {
      [labelActive]: {
        [key]: { $set: true },
      },
    });
    setTouched(updatedTouched);
  };

  const handleCancel = () => {
    setVehicle(null);
    setIsAddingVehicle(false);
  };

  const formatVehiclePayload = (values) => {
    const payload = values;
    payload[labelActive].input_method_type = labelActive;

    const setCustomValue = (fieldName) => {
      const fieldValue = payload[labelActive][fieldName];
      if (fieldValue && fieldValue === 'other') {
        payload[labelActive][fieldName] = payload.custom?.[fieldName] || '';
      }
    };

    // Modify payload for heavy tab to account for custom fields
    if (labelActive === 'heavy') {
      const {
        make,
        heavy_duty_engine_make: heavyEngineMake,
        fuel_type: fuelType,
      } = payload.heavy || {};

      if (make && make === 'Other') {
        payload.heavy.make = payload?.custom?.heavyMake || '';
      }

      if (fuelType && fuelType === 'Other') {
        payload.heavy.fuel_type = payload?.custom?.fuelType || '';
      }

      if (heavyEngineMake && heavyEngineMake === 'Other') {
        payload.heavy.heavy_duty_engine_make =
          payload?.custom?.heavyEngineMake || '';
      }

      payload.heavy = {
        ...payload.heavy,
        photo_filenames: payload.photo_filenames,
        skip_motor_lookup: true,
      };
    }

    // Modify payload for year tab to account for custom fields
    if (labelActive === 'year') {
      const { make, model, trim } = payload.year || {};
      if (make) {
        if (make === 'other') {
          setCustomValue('make');
        } else {
          payload[labelActive].make = (find(makeList, ['id', make]) || {}).name;
        }
      }

      if (model) {
        if (model === 'other') {
          setCustomValue('model');
        } else {
          payload[labelActive].model = (
            find(modelList, ['id', model]) || {}
          ).name;
        }
      }

      if (trim) {
        if (trim === 'other') {
          setCustomValue('trim');
        } else {
          payload[labelActive].trim = (find(trimList, ['id', trim]) || {}).name;
        }
      }
    }

    // Modify payload for nonautomobile tab to account for custom fields
    if (nonautomobileTabs.includes(labelActive)) {
      setCustomValue('make');
      setCustomValue('model');
      payload[labelActive] = {
        ...payload[labelActive],
        skip_motor_lookup: true,
      };
    }

    return {
      customer_id: customerId,
      ...payload[labelActive],
    };
  };

  const createVehicle = (payload) => {
    setIsCreating(true);
    if (!customerId) {
      if (onError) onError();
      setIsCreating(false);
      return;
    }
    if (onCreating) onCreating();
    apiPost('/vehicle', payload)
      .then(async (json) => {
        if (json.Status === 'Failure') {
          throw new Error(json?.Message);
        } else if (onSuccess) {
          await Promise.resolve(onSuccess(json));
          setIsAddingVehicle(false);
        }
      })
      .catch((error) => {
        if (onError) onError();
        console.log(error?.message);
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const handleSubmit = (values) => {
    const payload = formatVehiclePayload(values);
    createVehicle(payload);
  };

  if (customerType === 'existing' && !isAddingVehicle) {
    return (
      <Box>
        <Grid container>
          <VehicleListSelection
            vehicleData={vehicleData}
            vehicleList={vehicleList}
            setVehicle={setVehicle}
            setIsAddingVehicle={setIsAddingVehicle}
            onSelectVehicle={onSelectVehicle}
            onSaveSelectedVehicle={onSaveSelectedVehicle}
            onSkip={onSkip}
          />
        </Grid>
      </Box>
    );
  }

  return (
    <FormWithRedirect
      resource="customer"
      record={vehicle}
      save={handleSubmit}
      render={(formProps) => (
        <>
          <VehicleInputMethodTabs
            tabToLabelMap={tabToLabelMap}
            labelActive={labelActive}
            setLabelActive={setLabelActive}
          />
          {labelActive === 'heavy' && (
            <HeavyDutyForm
              years={years}
              handleChange={handleChange}
              handleHeavyChange={handleHeavyChange}
              vehicle={vehicle}
              setIsPhotoUploading={setIsPhotoUploading}
            />
          )}
          {labelActive === 'license' && <LicenseForm />}
          {labelActive === 'year' && (
            <YMMTForm
              years={years}
              makeList={makeList}
              modelList={modelList}
              trimList={trimList}
              vehicle={vehicle}
              touched={touched}
              handleTouched={handleTouched}
              handleChange={handleChange}
              loadMakes={loadMakes}
              loadModels={loadModels}
              loadTrims={loadTrims}
              setTrimList={setTrimList}
              setModelList={setModelList}
            />
          )}
          {labelActive === 'vin' && <VINForm />}
          {nonautomobileTabs.includes(labelActive) && (
            <NonAutomobileForm
              labelActive={labelActive}
              years={years}
              makeList={makeList}
              modelList={modelList}
              vehicle={vehicle}
              touched={touched}
              handleTouched={handleTouched}
              handleChange={handleChange}
              setModelList={setModelList}
              nonautomobileData={nonautomobileData}
            />
          )}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <DialogActions>
              <SaveButton
                className={classes.buttonWithoutIcon}
                label="Continue"
                handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                saving={isCreating}
                disabled={isCreating || isPhotoUploading}
              />
              {vehicleList.length ? (
                <Button onClick={handleCancel} label="Cancel">
                  <ClearIcon />
                </Button>
              ) : (
                onSkip && <Button onClick={onSkip} label="Skip" />
              )}
            </DialogActions>
          </Box>
        </>
      )}
    />
  );
};

export default AddVehicleForm;
